/**
 * Utility functions to access authentication data from localStorage
 * These functions do not trigger React re-renders
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  ID_TOKEN: 'idToken',
  USER_ROLES: 'userRoles',
  USER: 'user',
  IS_AUTHENTICATED: 'isAuthenticated',
  ORG_ID: 'orgId',
} as const;

/**
 * Get access token from localStorage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Get ID token from localStorage
 */
export const getIdToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
};

/**
 * Get user roles from localStorage
 */
export const getUserRoles = (): string[] => {
  try {
    const roles = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
    return roles ? JSON.parse(roles) : [];
  } catch (error) {
    console.error('Error parsing user roles:', error);
    return [];
  }
};

/**
 * Get user data from localStorage
 */
export const getUser = (): Record<string, unknown> | null => {
  try {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get authenticated user's Auth0 ID (sub claim) from localStorage.
 * This is a non-hook alternative to useAuthUserId for use outside React components.
 *
 * @returns {string} The user's Auth0 ID (sub claim), or empty string if not authenticated
 *
 * @example
 * ```typescript
 * const userId = getUserId();
 * // Returns: "auth0|69019a2365148bd755e83a77" or ""
 * ```
 */
export const getUserId = (): string => {
  const user = getUser();
  return (user?.sub as string) || '';
};

/**
 * Get the Auth0 organization ID (org_id claim) from localStorage.
 * Stored explicitly from the ID token claims when the user authenticates.
 * Falls back to the user object's org_id for backwards compatibility.
 *
 * @returns {string} The Auth0 organization ID, or empty string if not available
 *
 * @example
 * ```typescript
 * const orgId = getOrgId();
 * // Returns: "org_LjrNB9i401WPAdtp" or ""
 * ```
 */
export const getOrgId = (): string => {
  const orgId = localStorage.getItem(STORAGE_KEYS.ORG_ID);
  if (orgId) return orgId;
  // Fallback: read from stored user object for backwards compatibility
  const user = getUser();
  return (user?.org_id as string) || '';
};

/*
|--------------------------------------------------------------------------
| Silent Token Refresher
| Stores a reference to Auth0's getAccessTokenSilently so the API
| interceptor can fetch a fresh token without reading stale localStorage.
|--------------------------------------------------------------------------
*/

type TokenRefresher = () => Promise<string>;
let _tokenRefresher: TokenRefresher | null = null;

/**
 * Register a function that retrieves a fresh ID token for use outside React components.
 * Called once by useUserAuth when the user is authenticated.
 */
export const setTokenRefresher = (fn: TokenRefresher | null): void => {
  _tokenRefresher = fn;
};

/**
 * Get a fresh ID token using the registered refresher.
 * Falls back to localStorage if no refresher has been registered yet.
 */
export const getTokenSilently = async (): Promise<string | null> => {
  if (_tokenRefresher) {
    try {
      return await _tokenRefresher();
    } catch {
      // Refresh failed — fall back to cached ID token from localStorage
      return getIdToken();
    }
  }
  return getIdToken();
};

/**
 * Check if user is authenticated
 */
export const isUserAuthenticated = (): boolean => {
  return localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED) === 'true';
};

/**
 * Clear all authentication and user identity data from localStorage.
 * Reads the userId before removing auth keys so the per-user cache can be cleaned up.
 */
export const clearAuthData = (): void => {
  // Capture userId BEFORE removing the 'user' key
  const userId = getUserId();
  if (userId) {
    localStorage.removeItem(`lupp-user-identity-${userId}`);
  }

  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.IS_AUTHENTICATED);
  localStorage.removeItem(STORAGE_KEYS.ORG_ID);
};

/**
 * Check if user has a specific role
 */
export const hasRole = (role: string): boolean => {
  const roles = getUserRoles();
  return roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (roles: string[]): boolean => {
  const userRoles = getUserRoles();
  return roles.some((role) => userRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (roles: string[]): boolean => {
  const userRoles = getUserRoles();
  return roles.every((role) => userRoles.includes(role));
};
